/**
 * ContactDialog — Feb 2026 refactor.
 *
 * Add/Edit Contact dialog extracted out of ContactsPage so the page can
 * focus on list / filter / row rendering. Zero behavior change vs.
 * the prior inline JSX — every flow that worked before still works:
 *   - Multi-group save & reopen (chips + MultiGroupPicker).
 *   - Family-collision dialog (driven by the parked save in the hook).
 *   - Business card upload + AI extract.
 *   - Tags input, spec_categories grid, vendor / contractor / inspector
 *     conditional blocks.
 *
 * Props:
 *   form: the bag returned by useContactForm() — all state + handlers.
 *   specCategories: [{prefix, name}]   for the contractor block.
 *   CATEGORIES: string[]               the canonical category list.
 *   catGroup: 'client' | 'contractor' | 'vendor' | 'inspector' | 'other'
 *             (computed from formData.category by the caller).
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Camera, Upload, X, Image, Loader2, Plus, Briefcase, Shield, DollarSign, Truck, Star,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import MultiGroupPicker from '@/components/MultiGroupPicker';
import GroupCollisionDialog from '@/components/GroupCollisionDialog';
import { categoryForKind, kindCarriesTrades, kindFromCategory, kindLabel } from '@/utils/partyKind';
import '@/styles/cards/mydesk-cards.css';

const TRADES_API = process.env.REACT_APP_BACKEND_URL;

export default function ContactDialog({ form, specCategories, CATEGORIES, catGroup }) {
  // DP270-A — trades come from Admin → Custom Lists → Trades, and each one
  // carries the Spec Categories it prices.
  const [tradeList, setTradeList] = useState([]);
  // DP273-P2a — the firm's own kinds, then Personal and Other.
  const [kindList, setKindList] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${TRADES_API}/api/custom-lists/trades`)
      .then(r => { if (alive) setTradeList(Array.isArray(r.data) ? r.data : (r.data?.trades || [])); })
      .catch(() => {});
    axios.get(`${TRADES_API}/api/contacts/kinds`)
      .then(r => { if (alive) setKindList(r.data?.kinds || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const {
    showAddContact, setShowAddContact,
    editingContact,
    formData, setFormData,
    newTag, setNewTag,
    uploadingCard, extractingCard,
    fileInputRef,
    collisionDialog, setCollisionDialog,
    setPendingSubmit,
    handleBusinessCardUpload,
    executePendingSubmit,
    handleSubmit,
    addTag, removeTag,
    options,
  } = form;

  // DP273-P2a — the Kind drives the form. A contact saved before the Kind
  // existed reads as whatever its old category word says.
  const kind = formData.party_kind || kindFromCategory(formData.category, formData);
  const pickKind = (next) => setFormData({
    ...formData,
    party_kind: next,
    // The category word is kept as the label, and only rewritten when it no
    // longer says the same thing as the kind.
    category: categoryForKind(next, formData.category),
  });
  const showTrades = kindCarriesTrades(kind);
  const showRole = kind === 'team';

  return (
    <>
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription>{editingContact ? 'Update contact information' : 'Add a new contact to your rolodex'}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info — Always shown */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="John" data-testid="contact-first-name" />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Smith" data-testid="contact-last-name" />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="ABC Construction" data-testid="contact-company" />
              </div>
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} placeholder="Project Manager" data-testid="contact-job-title" />
              </div>
            </div>

            {/* Groups — multi-group memberships (Feb 2026). */}
            <div className="space-y-2" data-testid="add-contact-group-section">
              <Label className="flex items-center gap-1.5">
                Groups
                <span className="text-[10px] font-normal text-muted-foreground">
                  (family, company, or organization — a contact can be in several)
                </span>
              </Label>
              <MultiGroupPicker
                value={formData.group_ids || (formData.group_id ? [formData.group_id] : [])}
                onChange={(gids) => setFormData({
                  ...formData,
                  group_ids: gids,
                  group_id: gids[0] || null,
                })}
                defaultType={formData.company ? 'company' : 'family'}
                defaultName={formData.company || formData.last_name || ''}
              />
              <p className="text-[10px] text-muted-foreground">
                Add as many groups as the contact belongs to. The starred chip is the primary —
                its name mirrors onto the contact&apos;s company field when it&apos;s a company-type group.
                Leave empty to save as Unassigned and complete later.
              </p>
            </div>

            {/* Business Card Upload */}
            <div className="space-y-2 p-4 border border-dashed rounded-lg bg-muted/30">
              <Label className="flex items-center gap-2"><Camera className="w-4 h-4" />Business Card</Label>
              <div className="flex items-center gap-4">
                {formData.business_card_image ? (
                  <div className="relative">
                    <img src={formData.business_card_image} alt="Business card" className="w-32 h-20 object-cover rounded border" />
                    <Button size="sm" variant="destructive" className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={() => setFormData({ ...formData, business_card_image: '' })}><X className="w-3 h-3" /></Button>
                  </div>
                ) : (
                  <div className="w-32 h-20 border-2 border-dashed rounded flex items-center justify-center bg-muted/50"><Image className="w-6 h-6 text-muted-foreground" /></div>
                )}
                <div className="flex-1">
                  <input type="file" ref={fileInputRef} accept="image/*" capture="environment" onChange={handleBusinessCardUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingCard || extractingCard}>
                    {uploadingCard ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) :
                     extractingCard ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Extracting...</>) :
                     (<><Upload className="w-4 h-4 mr-2" />Upload Card</>)}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">Take a photo or upload an image. AI will extract contact info.</p>
                </div>
              </div>
            </div>

            {/* Kind — DP273-P2a. What this contact IS to the firm. The old
                category word stays on the record as its label. */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kind *</Label>
                <div className="flex flex-wrap gap-2" data-testid="contact-kind-group">
                  {kindList.map(k => (
                    <Button
                      key={k.key}
                      type="button"
                      size="sm"
                      variant={kind === k.key ? 'default' : 'outline'}
                      onClick={() => pickKind(k.key)}
                      data-testid={`contact-kind-${k.key}`}
                    >
                      {k.label || k.key}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground" data-testid="contact-kind-note">
                  What this contact is to the firm. Kept as a label: {formData.category || 'Other'}
                  {formData.category && !CATEGORIES.includes(formData.category) ? ' · your own word' : ''}.
                </p>
              </div>
              {/* Trade — the kinds that do work pick from the one list. */}
              {showTrades && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Briefcase className="w-4 h-4" />Trade</Label>
                  <div className="flex flex-wrap items-center gap-1" data-testid="contact-trades">
                    {(formData.trades || []).map(name => (
                      <Badge key={name} variant="secondary" className="flex items-center gap-1" data-testid={`contact-trade-${name}`}>
                        {name}
                        <button type="button" aria-label={`Remove ${name}`} data-testid={`contact-trade-remove-${name}`}
                          onClick={() => setFormData({ ...formData, trades: (formData.trades || []).filter(t => t !== name) })}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {formData.trade_unmatched && !(formData.trades || []).length && (
                      <Badge variant="outline" className="flex items-center gap-1 text-xs" data-testid="contact-trade-unmatched">
                        {formData.trade_unmatched}
                        <span className="text-muted-foreground">· unmatched · pick a trade</span>
                      </Badge>
                    )}
                    <select
                      value=""
                      onChange={(e) => {
                        const name = e.target.value;
                        if (!name || (formData.trades || []).includes(name)) return;
                        setFormData({ ...formData, trades: [...(formData.trades || []), name] });
                      }}
                      className="h-8 rounded-md border px-2 text-sm"
                      data-testid="contact-trade-add"
                    >
                      <option value="">+ add trade</option>
                      {tradeList
                        .filter(t => !(formData.trades || []).includes(t.name))
                        .map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                    </select>
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="contact-trade-prices">
                    Prices these categories:{' '}
                    {(() => {
                      const codes = [...new Set((formData.trades || []).flatMap(name => (
                        tradeList.find(t => t.name === name)?.spec_codes || []
                      )))];
                      if (!codes.length) return 'none yet';
                      return codes.map(code => {
                        const label = specCategories?.find(c => c.prefix === code)?.name;
                        return label ? `${code} · ${label}` : code;
                      }).join(', ');
                    })()}
                  </p>
                </div>
              )}
              {/* Role — a team member does a job here, not a trade. */}
              {showRole && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Briefcase className="w-4 h-4" />Role</Label>
                  <Input
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="Project Manager"
                    data-testid="contact-role"
                  />
                  <p className="text-xs text-muted-foreground">What they do here. Saved as the job title.</p>
                </div>
              )}
            </div>

            {/* === Contractor-specific fields === */}
            {(kind === 'gc' || kind === 'sub' || kind === 'interior_designer') && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2"><Shield className="w-4 h-4" />Contractor Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>License Number</Label>
                    <Input value={formData.license_number} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} data-testid="contact-license" />
                  </div>
                  <div className="space-y-2">
                    <Label>Insurance Info</Label>
                    <Input value={formData.insurance_info} onChange={(e) => setFormData({ ...formData, insurance_info: e.target.value })} data-testid="contact-insurance" />
                  </div>
                </div>

                {/* Spec Categories */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Spec Categories <Badge variant="secondary" className="text-xs">{formData.spec_categories?.length || 0} selected</Badge>
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-muted/30">
                    {specCategories.map(cat => (
                      <label key={cat.prefix}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          formData.spec_categories?.includes(cat.prefix) ? 'bg-primary/10 border border-primary' : 'hover:bg-muted'
                        }`}>
                        <Checkbox checked={formData.spec_categories?.includes(cat.prefix)}
                          onCheckedChange={(checked) => {
                            if (checked) setFormData({ ...formData, spec_categories: [...(formData.spec_categories || []), cat.prefix] });
                            else setFormData({ ...formData, spec_categories: (formData.spec_categories || []).filter(p => p !== cat.prefix) });
                          }} />
                        <div className="text-sm"><span className="font-mono text-xs text-muted-foreground">{cat.prefix}</span><span className="ml-1">{cat.name}</span></div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* === Vendor-specific fields === */}
            {kind === 'supplier' && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2"><DollarSign className="w-4 h-4" />Vendor Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Account Number</Label>
                    <Input value={formData.vendor_account_number} onChange={(e) => setFormData({ ...formData, vendor_account_number: e.target.value })} data-testid="contact-vendor-account" />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Terms</Label>
                    <Select value={formData.payment_terms || ''} onValueChange={(v) => setFormData({ ...formData, payment_terms: v })}>
                      <SelectTrigger data-testid="contact-payment-terms"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="COD">COD</SelectItem>
                        <SelectItem value="Net 15">Net 15</SelectItem>
                        <SelectItem value="Net 30">Net 30</SelectItem>
                        <SelectItem value="Net 60">Net 60</SelectItem>
                        <SelectItem value="Net 90">Net 90</SelectItem>
                        <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Truck className="w-4 h-4" />Preferred Shipping</Label>
                    <Input value={formData.preferred_shipping} onChange={(e) => setFormData({ ...formData, preferred_shipping: e.target.value })}
                      placeholder="Ground, Freight, Will Call" data-testid="contact-shipping" />
                  </div>
                  <div className="space-y-2">
                    <Label>Minimum Order ($)</Label>
                    <Input type="number" value={formData.minimum_order} onChange={(e) => setFormData({ ...formData, minimum_order: e.target.value })} data-testid="contact-min-order" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Return Policy</Label>
                  <Textarea value={formData.return_policy} onChange={(e) => setFormData({ ...formData, return_policy: e.target.value })} rows={2} data-testid="contact-return-policy" />
                </div>

                {/* Sales Rep */}
                <h4 className="text-sm font-semibold text-muted-foreground mt-4">Sales Representative</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Rep Name</Label>
                    <Input value={formData.rep_name} onChange={(e) => setFormData({ ...formData, rep_name: e.target.value })} data-testid="contact-rep-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Rep Email</Label>
                    <Input type="email" value={formData.rep_email} onChange={(e) => setFormData({ ...formData, rep_email: e.target.value })} data-testid="contact-rep-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>Rep Phone</Label>
                    <Input value={formData.rep_phone} onChange={(e) => setFormData({ ...formData, rep_phone: e.target.value })} data-testid="contact-rep-phone" />
                  </div>
                </div>

                {/* Vendor Rating */}
                <div className="flex items-center gap-4 mt-2">
                  <Label>Rating</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button key={s} type="button" onClick={() => setFormData({ ...formData, vendor_rating: s })}
                        className={`p-1 ${(formData.vendor_rating || 0) >= s ? 'text-amber-500' : 'text-muted-foreground/30'}`} data-testid={`vendor-star-${s}`}>
                        <Star className="w-5 h-5" fill={(formData.vendor_rating || 0) >= s ? 'currentColor' : 'none'} />
                      </button>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 ml-4 text-sm">
                    <Checkbox checked={formData.is_favorite_vendor} onCheckedChange={(c) => setFormData({ ...formData, is_favorite_vendor: !!c })} />
                    Favorite Vendor
                  </label>
                </div>
              </div>
            )}

            {/* === Inspector-specific fields === */}
            {catGroup === 'inspector' && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2"><Shield className="w-4 h-4" />Inspector Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>License Number</Label>
                    <Input value={formData.license_number} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} />
                  </div>
                </div>
              </div>
            )}

            {/* Tags — Always shown */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Add tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} data-testid="contact-new-tag" />
                <Button type="button" variant="outline" onClick={addTag} data-testid="contact-add-tag-btn"><Plus className="w-4 h-4" /></Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}<X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Contact Info — Always shown */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" data-testid="contact-email" />
              </div>
              <div className="space-y-2">
                <Label>Phone (Primary)</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="contact-phone" />
              </div>
              <div className="space-y-2">
                <Label>Mobile Phone</Label>
                <Input value={formData.phone_mobile} onChange={(e) => setFormData({ ...formData, phone_mobile: e.target.value })} data-testid="contact-phone-mobile" />
              </div>
              <div className="space-y-2">
                <Label>Work Phone</Label>
                <Input value={formData.phone_work} onChange={(e) => setFormData({ ...formData, phone_work: e.target.value })} data-testid="contact-phone-work" />
              </div>
            </div>

            {/* Address — Always shown */}
            <div className="space-y-4">
              <Label className="text-muted-foreground">Address</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Input value={formData.address_street} onChange={(e) => setFormData({ ...formData, address_street: e.target.value })} placeholder="Street Address" data-testid="contact-street" />
                </div>
                <div className="space-y-2">
                  <Input value={formData.address_city} onChange={(e) => setFormData({ ...formData, address_city: e.target.value })} placeholder="City" data-testid="contact-city" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={formData.address_state} onChange={(e) => setFormData({ ...formData, address_state: e.target.value })} placeholder="State" data-testid="contact-state" />
                  <Input value={formData.address_zip} onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })} placeholder="ZIP" data-testid="contact-zip" />
                </div>
              </div>
            </div>

            {/* Professional Info — shown for client/contractor/inspector */}
            {(kind === 'client' || kind === 'gc' || kind === 'sub'
              || kind === 'interior_designer' || catGroup === 'inspector') && (
              <div className="grid grid-cols-2 gap-4">
                {catGroup !== 'inspector' && (
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://" data-testid="contact-website" />
                  </div>
                )}
              </div>
            )}

            {/* Notes — Always shown */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder="Additional notes..." data-testid="contact-notes" />
            </div>

            {/* Iter 291 — Per-contact share_mode override. Category
                decides the default (shared for Client/GC/Sub/Vendor/Inspector;
                private for Personal/Business General/Other). Users can
                override on a per-contact basis when the admin allows it. */}
            <div className="flex items-start gap-3 p-3 rounded border border-[#2A2A2A] bg-[#141414]">
              <div className="flex-1 min-w-0">
                <Label className="text-sm font-medium">
                  Sharing
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  Shared contacts are visible to your team and sync to every teammate&apos;s Google.
                  Private contacts stay yours — only visible to you and only in your own Google.
                  Leave &ldquo;Use category default&rdquo; to follow the admin&apos;s rule for {formData.category || 'this category'}.
                </p>
                <select
                  value={formData.share_mode_override || ''}
                  onChange={(e) => setFormData({ ...formData, share_mode_override: e.target.value || null })}
                  className="mt-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs w-full"
                  data-testid="contact-share-mode-override"
                >
                  <option value="">Use category default</option>
                  <option value="shared">Shared with team</option>
                  <option value="private">Private (only me)</option>
                </select>
              </div>
            </div>
          </div>

          {/* DP273-P2c — opened from a memo or an email with a project in
              view: say plainly what adding them to the team will do. */}
          {options?.project && (
            <p className="text-xs text-muted-foreground px-1" data-testid="contact-add-to-team-note">
              Adding to {options.project.name || options.project.short_name || 'the project'} as{' '}
              {kindLabel(kind, kindList)}{(formData.trades?.[0] || formData.trade)
                ? ` · ${formData.trades?.[0] || formData.trade}` : ''}.
              The agreement and documents are asked for from the team row.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContact(false)} data-testid="contact-form-cancel">Cancel</Button>
            {options?.project ? (
              <>
                <Button variant="outline" onClick={handleSubmit} data-testid="contact-form-submit">Just save</Button>
                <button
                  type="button"
                  className="kmc-btn kmc-btn--primary"
                  onClick={() => handleSubmit(true)}
                  data-testid="contact-save-and-add"
                >
                  Save and add to team
                </button>
              </>
            ) : (
              <Button onClick={handleSubmit} data-testid="contact-form-submit">{editingContact ? 'Update Contact' : 'Add Contact'}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Same-name family disambiguation prompt. */}
      <GroupCollisionDialog
        open={collisionDialog.open}
        onOpenChange={(v) => setCollisionDialog(s => ({ ...s, open: v }))}
        data={collisionDialog.data}
        onResolve={(gid) => executePendingSubmit(gid)}
        onCancel={() => setPendingSubmit(null)}
      />
    </>
  );
}
